import { React, useEffect, useState, useRef, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useParams } from "react-router-dom";
import Table from 'react-bootstrap/Table';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import "./index.scss";

import RadioButton from "../../../ui/RadioButton/index.js";
import Button from "../../../../components/ui/Button";
import DropDown from "../../../ui/DropDown/index.js";
import SkeletonLoader from "../../../ui/SkeletonLoader/index.js";

import Pagination from '../../../ui/Pagination';
import ProfileAvatar from "../../../ui/ProfileAvatar";
import NoDataFound from "../../../ui/NoDataFound";

import { filterIcon, filterCloseIcon, searchIcon } from "../../../../utils/icons.js";
import { formatDate, formatRelativeTime, getCountryImageUrl } from "../../../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getUserSessionById } from '../../../../store/actions/usersAction.js';

const filterRadioOption = [    
    {
        label: "Ascending",
        value: "asc"
    },
    {
        label: "Descending",
        value: "desc"
    }
]

const sortByOption = [
    {
        label: "User Agent",
        value: "user_agent"
    },
    {
        label: "Last Accessed",
        value: "last_accessed"
    },
    {
        label: "IP Address",
        value: "ip_address"
    },
    {
        label: "Region",
        value: "region"
    },
    {
        label: "Country",
        value: "country"
    },
    {
        label: "State",
        value: "state"
    },
    {
        label: "City",
        value: "city"
    },
]


const UserSessionTab = () => {

    const userSessionList = useSelector((state) => state.users?.userSessionById);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { userId } = useParams();

    const filterWrapperRef = useRef(null);

    const [userSessionSearchValue, setUserSessionSearchValue] = useState("");
    const [searchedUserSessionValue, SetSearchedUserSessionValue] = useState("")
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false)

    //filter value
    const [showFilter, setShowFilter] = useState(false);
    const [selectedSortby, setSelectedSortby] = useState("");
    const [selectedSortOrder, setSelectedSortOrder] = useState("");

    
    //selected filter value
    const [filterSelectedSortby, setFilterSelectedSortby] = useState("");
    const [filterSelectedSortOrder, setFilterSelectedSortOrder] = useState("");
    

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && isSearchFocused) {
            searchUserSession()
        }
    }

    const searchUserSession = () => {
        setCurrentPage(1);
        SetSearchedUserSessionValue(userSessionSearchValue)
    }

    const removeFilter = (type) => {
        setCurrentPage(1);
        if (type === "sortOrder") {
            setSelectedSortOrder("")
            setFilterSelectedSortOrder("")
        } else {
            setSelectedSortby("")
            setSelectedSortOrder("")
            setFilterSelectedSortby("")
            setFilterSelectedSortOrder("")
        }
    }

    const applyFilter = () => {
        setCurrentPage(1);
        setShowFilter(false)
        setFilterSelectedSortby(selectedSortby);
        setFilterSelectedSortOrder(selectedSortOrder);
    }

    const sortOrderChange = (data) => {
        setSelectedSortOrder(data)
    }

    const displaySortByName = (type) => {
        if (type === "sortOrder") {
            const matchedOption = filterRadioOption.find((data) => data.value === selectedSortOrder)
            return matchedOption ? matchedOption.label : "";
        } else {
            const matchedOption = sortByOption.find((data) => data.value === selectedSortby)
            return matchedOption ? matchedOption.label : "";
        }
    }

    const filterClicked = () => {
        setShowFilter(true)
    }

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const fetchUserSession = useCallback(()=>{
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}&user_id=${userId}`;
        const queryParams = searchedUserSessionValue ? `&query=${searchedUserSessionValue}` : "";
        const sortParams = (filterSelectedSortby ? `&sort_by=${filterSelectedSortby}` : '') + (filterSelectedSortOrder ? `&sort_order=${filterSelectedSortOrder}` : '');
        const url = `${baseUrl}${queryParams}${sortParams}`;
        setIsLoading(true)
        dispatch(getUserSessionById(url))
    },[currentPage, currentPageSize, dispatch, userId, filterSelectedSortby, filterSelectedSortOrder, searchedUserSessionValue])

    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside);

        function handleClickOutside(event) {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                if(showFilter){
                    setShowFilter(false)
                }
            }        
        }
    },[showFilter])

    useEffect(()=>{
        fetchUserSession()
    },[fetchUserSession])

    useEffect(()=>{
        if(userSessionList !== null){
            setIsLoading(false)
        }
    },[userSessionList])


    return (
        <Row>
            <Col md={12}>
                <div className="search-filter-container my-3 d-flex justify-content-between align-items-center">
                    <div className="search-container d-flex align-items-center gap-3">
                        <div className="position-relative" onKeyDown={handleSearchKeyDown}>
                            <input className="search-input rounded" 
                                placeholder="Search Session" 
                                value={userSessionSearchValue} 
                                onChange={(e)=>setUserSessionSearchValue(e.target.value)}
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}/>
                            <span className="position-absolute search-icon">{searchIcon()}</span>
                        </div>
                        <button className="search-btn">Search</button>
                        <div className="position-relative">
                            <span className="filter-icon" onClick={filterClicked}>{filterIcon()}</span>
                            {showFilter && <div className="filters-container position-absolute p-3 rounded" ref={filterWrapperRef}>
                                <h6>{t("organizationDetailsPage.usersTable.sortBy")}</h6>
                                <DropDown 
                                    placeHolder={"Sort By"}
                                    options={sortByOption}
                                    isMulti={false}
                                    isSearchable={false}
                                    onChange={(data)=>{setSelectedSortby(data.value)}}
                                    defaultSelectedValue={selectedSortby}
                                />
                                <h6 className="mt-4">Sort Order</h6>
                                <div className="filter-user-status d-flex mb-4 gap-4">
                                    {
                                        filterRadioOption.map((data,index)=>{
                                            return (
                                                <RadioButton radioId={data.label + index} radioValue={data.value} radioLabel={data.label} onChange={sortOrderChange} key={index} isChecked={data.value === selectedSortOrder} />
                                            )
                                        })
                                    }
                                </div>
                                <div className="filter-apply-btn mt-3">
                                    <Button buttonText={"Apply"}  buttonType={"primary"} disableButton={false} buttonClicked={applyFilter}/>
                                </div>
                            </div>}
                        </div>
                        {!showFilter && <p className="selected-filters">
                            {filterSelectedSortby !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortby")} <span onClick={() => removeFilter("sortby")}>{filterCloseIcon()}</span></span>}
                            {filterSelectedSortOrder !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortOrder")} <span onClick={() => removeFilter("sortOrder")}>{filterCloseIcon()}</span></span>}
                        </p>}
                    </div>                    
                </div>
                <div className="domain-table-container user-page-org-table">
                    {
                        isLoading ?
                        <SkeletonLoader type={"line"} count={5} currentPage={"userSession"} />
                        :
                        <>
                            <Table responsive="lg">
                                <thead>
                                    <tr>
                                        <th>{t("accountSettingPage.userSessionTab.userAgent")}</th>
                                        <th>{t("accountSettingPage.userSessionTab.lastAccessed")}</th>
                                        <th>{t("accountSettingPage.userSessionTab.lastLogin")}</th>
                                        <th>{t("accountSettingPage.userSessionTab.ipAddress")}</th>
                                        <th>{t("accountSettingPage.userSessionTab.address")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        userSessionList?.results.length > 0 ? 
                                        userSessionList?.results.map((data, index)=>{
                                            return (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                                >
                                                    <td className="system-name">{data.user_agent} {data.current_session ? <span>CURRENT SESSION</span> : ""}</td>
                                                    <td>{formatRelativeTime(data?.last_accessed)}</td>
                                                    <td>{formatDate(data?.last_accessed)}</td>
                                                    <td>
                                                        <p>{data.ip_address}</p>
                                                    </td>
                                                    <td>
                                                        <ProfileAvatar type={"profile"} firstName={`${data?.city}, ${data?.region}`} lastName={""} secondaryText={`${data.country}, ${data.continent}`} imageUrl={getCountryImageUrl(data)}/>
                                                    </td>
                                                </motion.tr>
                                            )
                                        }) 
                                        
                                        :                                             
                                            <motion.tr
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4, delay: 1 * 0.1 }}
                                            >
                                                <td colSpan="5" style={{ textAlign: 'center' }}>
                                                    <NoDataFound noDataText={"No data Found"}/>
                                                </td>
                                            </motion.tr>
                                    }
                                </tbody>
                            </Table>
                            {userSessionList != null && userSessionList?.results.length > 0 && <Pagination
                                    className="pagination-bar"
                                    currentPage={currentPage}
                                    pageSize={currentPageSize}
                                    totalCount={userSessionList.meta.total_results}
                                    onPageChange={setCurrentPageandRecordSize}
                                />}
                        </>
                    }
                </div>
            </Col>
        </Row>
    )
}

export default UserSessionTab;